import {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbSeparator,
  BreadcrumbPage,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@capacit-com/lara-law-shared-ui";
import {
  SidebarTrigger,
  useRightSidebarContent,
} from "@capacit-com/lara-law-shared-ui";
import { cn } from "@/lib/utils";
import useSidebarStore from "@/stores/sidebar.store";
import {
  claimRightSidebarContent,
  releaseRightSidebarContent,
} from "@/lib/sidebar/rightSidebarOwner";
import {
  PropsWithChildren,
  useCallback,
  useEffect,
  useId,
  useRef,
  useMemo,
} from "react";
import React from "react";
import NotificationButton from "@/components/notification/NotificationButton";
import { useNavigate, useLocation } from "react-router-dom";
import SynonymButton from "@/components/synonym/SynonymButton";
import { GlobalSearchAppBar } from "./global-search/GlobalSearchAppBar";
import type { BreadcrumbConfig } from "@/config/documentViewConfig";
import TruncatedText from "@/components/text/TruncatedText";
import { ChevronLeft, ChevronRight, MoreHorizontal } from "lucide-react";
import { useBreadcrumbParentCapacity } from "@/hooks/useBreadcrumbParentCapacity";
import { collapseBreadcrumbParents } from "@/lib/navigation/collapseBreadcrumbParents";

interface PageContentProps {
  title: string;
  /** Full title for tooltips when the display title is truncated. */
  titleFull?: string;
  /** Single parent or array of parents for multi-level breadcrumbs */
  parent?: BreadcrumbConfig | BreadcrumbConfig[];
  /** Uncollapsed parents for compact md path menu in document viewers */
  parentOverflowMenu?: BreadcrumbConfig[];
  /** Document viewer back — shown in the app bar beside breadcrumbs. */
  onNavigateBack?: () => void;
  rightSidebar?: React.ReactNode;
  appSidebar?: boolean;
  customAppBarContent?: React.ReactNode;
  contentClassName?: string;
  hasAppBar?: boolean;
  appFooter?: React.ReactNode;
  appFooterClassName?: string;
  displaySearchBar?: boolean;
}


const BreadcrumbTrailSeparator = ({ className }: { className?: string }) => (
  <BreadcrumbSeparator
    className={cn(
      "inline-flex size-5 shrink-0 items-center justify-center",
      className
    )}
  >
    <ChevronRight className="size-3.5 text-muted-foreground" strokeWidth={2} />
  </BreadcrumbSeparator>
);

const getParentMaxWidthClass = (widthTier?: BreadcrumbConfig["widthTier"]) =>
  widthTier === "document"
    ? "max-w-[35%] lg:max-w-[8rem] xl:max-w-[10rem]"
    : "max-w-[40%] lg:max-w-[10rem] xl:max-w-[12rem]";

/** Document viewer crumbs stay hidden until lg; regular pages show from md. */
const getBreadcrumbParentVisibilityClass = (breadcrumb: BreadcrumbConfig) =>
  breadcrumb.widthTier != null || breadcrumb.kind === "ellipsis"
    ? "hidden lg:inline-flex"
    : "hidden md:inline-flex";

/**
 * Helper function to generate a stable identifier for React.ReactNode
 * Used to compare sidebar content without causing re-renders
 * Includes props in the key to differentiate between instances with different props
 */
const getSidebarKey = (sidebar: React.ReactNode): string | null => {
  if (!sidebar) return null;

  // If it's a React element, try to get a stable identifier
  if (React.isValidElement(sidebar)) {
    const element = sidebar as React.ReactElement;
    let typeName = "Unknown";

    // Get component type name
    if (typeof element.type === "function") {
      const fn = element.type as
        | React.FunctionComponent<unknown>
        | (React.ComponentClass<unknown, unknown> & {
            displayName?: string;
            name?: string;
          });
      typeName = (fn.displayName ?? fn.name) || "Unknown";
    } else if (typeof element.type === "string") {
      typeName = element.type;
    }

    // Include key if available
    const key = element.key ? `-key:${element.key}` : "";

    // Include a hash of important props to differentiate instances.
    // Must cover props that change after mount (load / revision) so the right-sidebar
    // portal receives updated elements — identity keys alone are not enough.
    // Short load/presence signals come first; do not truncate the joined key.
    const props = element.props as Record<string, unknown>;
    let propsHash = "";
    if (props) {
      const availableTabsKey = Array.isArray(props.availableTabs)
        ? (props.availableTabs as unknown[]).map(String).join(",")
        : props.availableTabs;
      const importantProps = [
        props.isLoading === true ? "loading" : "ready",
        props.legislation != null ? "hasLegislation" : "noLegislation",
        props.workspaceDocument != null
          ? "hasWorkspaceDocument"
          : "noWorkspaceDocument",
        props.tocItems != null ? "hasTocItems" : "noTocItems",
        props.documentId,
        props.selectionResetKey,
        props.contextualDefault,
        availableTabsKey,
        props.blobId,
        props.currentRevisionVersion,
      ]
        .filter((p) => p !== undefined && p !== null && p !== "")
        .map((p) => String(p))
        .join("|");
      if (importantProps) {
        propsHash = `-props:${importantProps}`;
      }
    }

    return `${typeName}${key}${propsHash}`;
  }

  // For other cases, return a hash of the string representation
  return String(sidebar);
};

const PageContent = ({
  title,
  titleFull,
  parent,
  parentOverflowMenu,
  onNavigateBack,
  rightSidebar,
  appSidebar = true,
  customAppBarContent,
  children,
  contentClassName,
  hasAppBar = true,
  appFooter,
  appFooterClassName,
  displaySearchBar = true,
}: PropsWithChildren<PageContentProps>) => {
  const { setContent: setRightSidebarContent } = useRightSidebarContent();
  const { appSidebarVisible: currentAppSidebarVisible, setAppSidebarVisible, rightSidebarToggleDisabled } =
    useSidebarStore();
  const navigate = useNavigate();
  const location = useLocation();
  /** Identifies this instance as the owner of the shared right-sidebar content slot. */
  const rightSidebarOwnerId = useId();

  const handleParentNavigate = useCallback(
    (breadcrumb: BreadcrumbConfig) => {
      breadcrumb.onClick?.();
      navigate(breadcrumb.url);
    },
    [navigate]
  );


  const renderCompactPathMenu = useCallback(
    (items: BreadcrumbConfig[]) => {
      if (items.length === 0) {
        return null;
      }

      return (
        <BreadcrumbItem className="shrink-0 md:inline-flex lg:hidden items-center overflow-visible">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="icon-sm"
                className="size-7 shrink-0 p-0 focus-visible:ring-inset focus-visible:ring-[2px]"
                aria-label="Vis navigationstrin"
                id="document-viewer-breadcrumb-compact-menu"
              >
                <MoreHorizontal className="size-4 text-muted-foreground" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="start"
              className="max-w-sm max-h-64 overflow-y-auto"
            >
              {items.map((item, itemIndex) => (
                <DropdownMenuItem
                  key={`${item.url}-${itemIndex}`}
                  className="cursor-pointer"
                  onClick={() => handleParentNavigate(item)}
                >
                  <TruncatedText
                    text={item.fullTitle ?? item.title}
                    className="text-sm max-w-full"
                    side="right"
                    sideOffset={8}
                  />
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </BreadcrumbItem>
      );
    },
    [handleParentNavigate]
  );

  const renderBreadcrumbParent = useCallback(
    (p: BreadcrumbConfig, index: number) => {
      const fullLabel = p.fullTitle ?? p.title;

      if (p.kind === "ellipsis") {
        return (
          <React.Fragment key={`ellipsis-${index}`}>
            <BreadcrumbItem
              className={cn(
                getBreadcrumbParentVisibilityClass(p),
                "shrink-0 items-center overflow-visible"
              )}
            >
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon-sm"
                    className="size-7 shrink-0 p-0 focus-visible:ring-inset focus-visible:ring-[2px]"
                    aria-label="Vis mellemliggende trin"
                    id="document-viewer-breadcrumb-ellipsis"
                  >
                    <MoreHorizontal className="size-4 text-muted-foreground" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align="start"
                  className="max-w-sm max-h-64 overflow-y-auto"
                >
                  {(p.hiddenParents ?? []).map((hidden, hiddenIndex) => (
                    <DropdownMenuItem
                      key={`${hidden.url}-${hiddenIndex}`}
                      className="cursor-pointer"
                      onClick={() => handleParentNavigate(hidden)}
                    >
                      <TruncatedText
                        text={hidden.fullTitle ?? hidden.title}
                        className="text-sm max-w-full"
                        side="right"
                        sideOffset={8}
                      />
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </BreadcrumbItem>
            <BreadcrumbTrailSeparator
              className={getBreadcrumbParentVisibilityClass(p)}
            />
          </React.Fragment>
        );
      }

      return (
        <React.Fragment key={`${p.url}-${index}`}>
          <BreadcrumbItem
            className={cn(
              getBreadcrumbParentVisibilityClass(p),
              "shrink-0 min-w-0 items-center overflow-hidden",
              getParentMaxWidthClass(p.widthTier)
            )}
          >
            <BreadcrumbLink
              onClick={(e) => {
                e.preventDefault();
                handleParentNavigate(p);
              }}
              href={p.url}
              title={fullLabel}
              className="block min-w-0"
            >
              <TruncatedText text={fullLabel} className="text-sm" />
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbTrailSeparator
            className={getBreadcrumbParentVisibilityClass(p)}
          />
        </React.Fragment>
      );
    },
    [handleParentNavigate]
  );

  // Create stable keys for sidebar content to use in dependencies
  // This allows us to detect changes without including the React node itself
  const rightSidebarKey = useMemo(
    () => getSidebarKey(rightSidebar),
    [rightSidebar]
  );
  const appSidebarValue = useMemo(
    () => appSidebar ?? true, // Normalize undefined to true (default)
    [appSidebar]
  );

  // Track previous values using refs to prevent unnecessary updates
  const prevRightSidebarKeyRef = useRef<string | null>(null);
  const prevAppSidebarRef = useRef<boolean | undefined>(undefined);
  const prevLocationRef = useRef<string>(location.pathname);
  const isInitialMountRef = useRef<boolean>(true);

  useEffect(() => {
    // Use current prop values directly to ensure we have the latest values
    const currentRightSidebar = rightSidebar;
    const currentRightSidebarKey = rightSidebarKey;
    const currentAppSidebar = appSidebarValue;
    const currentLocation = location.pathname;
    const locationChanged = prevLocationRef.current !== currentLocation;
    const isInitialMount = isInitialMountRef.current;

    // Determine if we need to update sidebars
    const rightSidebarKeyChanged =
      prevRightSidebarKeyRef.current !== currentRightSidebarKey;
    const appSidebarChanged = prevAppSidebarRef.current !== currentAppSidebar;

    // Check if current store state matches desired state
    const appSidebarShouldBeVisible = currentAppSidebar === true;
    const appSidebarStateMismatch =
      currentAppSidebarVisible !== appSidebarShouldBeVisible;

    // Update right sidebar only on: initial mount, location changed, or content key changed
    // Do NOT update just because user toggled (closed) the sidebar - respect user's choice
    const shouldUpdateRightSidebar =
      isInitialMount || locationChanged || rightSidebarKeyChanged;

    const shouldUpdateAppSidebar =
      isInitialMount ||
      locationChanged ||
      appSidebarChanged ||
      appSidebarStateMismatch;

    // Update right sidebar. Every write claims the shared content slot so the unmount
    // cleanup below can tell whether this page is still the one on screen.
    if (shouldUpdateRightSidebar) {
      if (currentRightSidebar) {
        // Show sidebar with new content
        setRightSidebarContent(currentRightSidebar);
        claimRightSidebarContent(rightSidebarOwnerId);
        prevRightSidebarKeyRef.current = currentRightSidebarKey;
      } else if (
        isInitialMount ||
        prevRightSidebarKeyRef.current !== null ||
        locationChanged
      ) {
        // Hide sidebar if we don't have one
        setRightSidebarContent(null);
        claimRightSidebarContent(rightSidebarOwnerId);
        prevRightSidebarKeyRef.current = null;
      }
    }

    // Update app sidebar visibility
    if (shouldUpdateAppSidebar) {
      if (currentAppSidebar) {
        setAppSidebarVisible(true);
        prevAppSidebarRef.current = currentAppSidebar;
      } else {
        setAppSidebarVisible(false);
        prevAppSidebarRef.current = false;
      }
    }

    // Update location ref after processing
    prevLocationRef.current = currentLocation;

    // Mark initial mount as complete after first run
    if (isInitialMount) {
      isInitialMountRef.current = false;
    }
  }, [
    location.pathname,
    rightSidebar,
    rightSidebarKey,
    appSidebarValue,
    currentAppSidebarVisible,
    rightSidebarOwnerId,
    setRightSidebarContent,
    setAppSidebarVisible,
  ]);

  // Cleanup on unmount.
  //
  // The right-sidebar node is rendered by `RightSidebarRenderer`, which sits outside
  // `<Routes>` and therefore outside every `AuthenticationGuard` — the stored element keeps
  // rendering after this page is gone unless we clear it (stale sidebar UI across
  // navigations, and previously a crash when a chat sidebar outlived its providers).
  //
  // Only clear while this instance still owns the slot: React can run this cleanup *after*
  // the incoming page's effect, and an unconditional clear would blank the sidebar that
  // page just set (and flicker when it did not).
  useEffect(() => {
    return () => {
      setAppSidebarVisible(false);

      if (releaseRightSidebarContent(rightSidebarOwnerId)) {
        setRightSidebarContent(null);
      }

      // These refs outlive the effects that own them when React replays mount effects on
      // the same instance (StrictMode's dev remount). Reset them so the next run counts as
      // an initial mount and re-establishes the content we just cleared.
      isInitialMountRef.current = true;
      prevRightSidebarKeyRef.current = null;
    };
  }, [rightSidebarOwnerId, setAppSidebarVisible, setRightSidebarContent]);

  const inputRef = React.useRef<HTMLInputElement>(
    null
  ) as React.RefObject<HTMLInputElement>;
  const headerRef = useRef<HTMLElement>(null);
  const breadcrumbListRef = useRef<HTMLDivElement>(null);

  const resolvedTitleFull = titleFull ?? title;
  const isDocumentViewerBreadcrumbs = Boolean(parentOverflowMenu?.length);

  const maxVisibleParents = useBreadcrumbParentCapacity({
    flatParents: parentOverflowMenu ?? [],
    headerRef,
    listRef: breadcrumbListRef,
    enabled: isDocumentViewerBreadcrumbs,
    remeasureKey: `${resolvedTitleFull}|${Boolean(onNavigateBack)}|${displaySearchBar}`,
  });

  const parentItems = useMemo(() => {
    if (isDocumentViewerBreadcrumbs && parentOverflowMenu) {
      return collapseBreadcrumbParents(parentOverflowMenu, maxVisibleParents);
    }

    if (!parent) {
      return [];
    }

    return Array.isArray(parent) ? parent : [parent];
  }, [
    isDocumentViewerBreadcrumbs,
    parent,
    parentOverflowMenu,
    maxVisibleParents,
  ]);

  return (
    <div className="flex flex-1">
      {/* min-w-0: this row-direction flex item would otherwise adopt a
          content-based minimum width during print, where PrintStyles resets
          overflow to visible — a fixed pixel width inside a document viewer
          then inflates the printed layout past the paper (LAR-396). */}
      <div className="flex flex-1 flex-col min-w-0 overflow-y-hidden">
        {hasAppBar && (
          <header
            ref={headerRef}
            className="sticky top-0 z-50 flex h-(--appbar-height) shrink-0 items-center gap-1 sm:gap-2 border-b px-2 sm:px-4 bg-background overflow-hidden min-w-0"
          >
            {currentAppSidebarVisible && (
              <>
                <SidebarTrigger
                  sidebar="left"
                  className="-ml-1"
                />
                <div className="h-4 w-px bg-border mx-2" />
              </>
            )}
            {customAppBarContent ? (
              <div className="flex items-center w-full gap-2">
                {customAppBarContent}
                {displaySearchBar ? (
                  <div className="flex gap-1 lg:gap-2">
                    <SynonymButton />
                    <NotificationButton />
                    <GlobalSearchAppBar inputRef={inputRef} />
                  </div>
                ) : (
                  <div className="flex gap-1 lg:gap-2">
                    <SynonymButton />
                    <NotificationButton />
                  </div>
                )}
              </div>
            ) : (
              <>
              <div className="flex min-w-0 flex-1 items-center gap-2 overflow-hidden">
                {onNavigateBack && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={onNavigateBack}
                    className="h-8 shrink-0 gap-1 px-2"
                    data-testid="document-viewer-back-button"
                    id="document-viewer-back-button"
                  >
                    <ChevronLeft className="size-4" />
                    <span>Tilbage</span>
                  </Button>
                )}
                <div
                  ref={breadcrumbListRef}
                  className="min-w-0 flex-1 overflow-hidden"
                >
                  <Breadcrumb className="min-w-0 overflow-hidden">
                  <BreadcrumbList className="flex flex-nowrap items-center gap-1 overflow-x-hidden overflow-y-visible sm:gap-1.5 [&_[data-slot=breadcrumb-item]]:items-center [&_[data-slot=breadcrumb-separator]]:items-center">
                    {parentOverflowMenu &&
                      renderCompactPathMenu(parentOverflowMenu)}
                    {parentOverflowMenu && parentOverflowMenu.length > 0 && (
                      <BreadcrumbTrailSeparator className="shrink-0 md:inline-flex lg:hidden" />
                    )}
                    {parentItems.map((p, index) =>
                      renderBreadcrumbParent(p, index)
                    )}
                    <BreadcrumbItem
                      className={cn(
                        "inline-flex min-w-0 items-center overflow-hidden",
                        isDocumentViewerBreadcrumbs
                          ? "min-w-0 flex-1 max-w-none"
                          : "max-w-[40%] shrink-0 sm:max-w-[45%] lg:max-w-[14rem] xl:max-w-[18rem]"
                      )}
                    >
                      <BreadcrumbPage
                        title={resolvedTitleFull}
                        className="block min-w-0 leading-none"
                      >
                        <TruncatedText
                          text={resolvedTitleFull}
                          className="text-sm"
                        />
                      </BreadcrumbPage>
                    </BreadcrumbItem>
                  </BreadcrumbList>
                </Breadcrumb>
                </div>
              </div>
                {displaySearchBar ? (
                  <div className="flex shrink-0 gap-1 lg:gap-2">
                    <SynonymButton />
                    <NotificationButton />
                    <GlobalSearchAppBar inputRef={inputRef} />
                  </div>
                ) : (
                  <div className="flex shrink-0 gap-1 lg:gap-2">
                    <SynonymButton />
                    <NotificationButton />
                  </div>
                )}
              </>
            )}
            {rightSidebar && (
              <>
                <div className="h-4 w-px bg-border mx-2" />
                <SidebarTrigger
                  id="right-sidebar-toggle"
                  sidebar="right"
                  variant="ghost"
                  size="icon-sm"
                  className="-mr-1"
                  disabled={rightSidebarToggleDisabled}
                  useShortcut={!rightSidebarToggleDisabled}
                />
              </>
            )}
          </header>
        )}
        <div
          data-content-scroll="true"
          className={cn(
            "flex flex-col gap-4 overflow-y-auto overflow-x-hidden",
            appFooter && hasAppBar
              ? "h-[calc(100vh-var(--appfooter-height)-var(--appbar-height))]"
              : hasAppBar && !appFooter
                ? "h-[calc(100vh-var(--appbar-height))]"
                : !hasAppBar && appFooter
                  ? "h-[calc(100vh-var(--appfooter-height))]"
                  : "h-full",
            contentClassName
          )}
          style={
            {
              "--doc-scroll-offset": "calc(var(--appbar-height) + 12px)",
            } as React.CSSProperties
          }
        >
          {children}
        </div>
        {appFooter && (
          <footer
            className={cn(
              "flex h-(--appfooter-height) shrink-0",
              appFooterClassName
            )}
          >
            {appFooter}
          </footer>
        )}
      </div>
    </div>
  );
};

export default PageContent;
